import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Link,
  useTheme,
  useMediaQuery,
  IconButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; 
import NewsCard from '../components/ui/NewsCard';
import apiService from '../services/api';
import { API_ENDPOINTS } from '../utils/apiUrls';

const toArray = (res) => (Array.isArray(res) ? res : res?.items ?? []);
const makeItemKey = (item) => item?.id ?? item?.url ?? `${item?.title ?? 'news'}-${item?.published_at ?? ''}`;

const NewsPreview = ({ limit = 3, spacing = 0.5, offsetTop = false }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(false);
  const sentinelRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const location = useLocation();
  const navigate = useNavigate();
  const isFullPage = location.pathname === '/news';
  const isOnsaMarketPage = location.pathname === '/onsa-market';

  const isDashboard = location.pathname === '/' || location.pathname === '/dashboard';
  const compactWidget = !isFullPage && !isMobile && isDashboard;

  const pageLimit = isFullPage ? 20 : limit;

  const cardBg = theme.palette.mode === 'light' ? '#f7f9fc' : 'rgba(255,255,255,0.04)';
  const cardLine = theme.palette.mode === 'light' ? 'rgba(2,6,23,.08)' : 'rgba(255,255,255,.12)';

  const fetchMore = useCallback(
    async ({ reset = false } = {}) => {
      if (!isFullPage) return;
      if (loadingRef.current) return;
      if (!reset && !hasMoreRef.current) return;

      loadingRef.current = true;
      setLoading(true);
      setError('');
      if (reset) {
        offsetRef.current = 0;
        hasMoreRef.current = true;
        setHasMore(true);
      }

      const requestOffset = reset ? 0 : offsetRef.current;
      const requestLimit = pageLimit; // always fetch 20 for full page
      try {
        const res = await apiService.get(API_ENDPOINTS.NEWS.GET_ALL, {
          limit: requestLimit,
          offset: requestOffset,
        });
        const items = toArray(res);
        const received = items.length;
        const total = Number(res?.meta?.total);
        const limitedItems = requestLimit ? items.slice(0, requestLimit) : items;

        setNews((prev) => {
          if (reset) return limitedItems;

          if (!prev.length) return limitedItems;

          const existingKeys = new Set(prev.map(makeItemKey));
          const toAppend = limitedItems.filter((item) => {
            const key = makeItemKey(item);
            if (!key) return true;
            if (existingKeys.has(key)) return false;
            existingKeys.add(key);
            return true;
          });
          return [...prev, ...toAppend];
        });

        offsetRef.current = requestOffset + received;
        const nextHasMore = Number.isFinite(total)
          ? requestOffset + received < total
          : received === requestLimit && received > 0;
        hasMoreRef.current = nextHasMore;
        setHasMore(nextHasMore);
      } catch (err) {
        console.error('News fetch error:', err);
        if (reset) setNews([]);
        setError('News data could not be loaded.');
        hasMoreRef.current = false;
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [isFullPage, pageLimit],
  );

  useEffect(() => {
    if (isFullPage) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiService.get(API_ENDPOINTS.NEWS.GET_ALL, {
          limit: pageLimit,
          offset: 0,
        });
        if (cancelled) return;
        setNews(toArray(res).slice(0, pageLimit));
      } catch (err) {
        console.error('News fetch error:', err);
        if (!cancelled) {
          setNews([]);
          setError('News data could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setHasMore(false);
          hasMoreRef.current = false;
          offsetRef.current = 0;
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isFullPage, pageLimit]);

  useEffect(() => {
    if (!isFullPage) return;
    hasMoreRef.current = true;
    fetchMore({ reset: true });
  }, [fetchMore, isFullPage]);

  useEffect(() => {
    if (!isFullPage) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (loadingRef.current) return;
        if (!hasMoreRef.current) return;
        fetchMore();
      },
      {
        root: null,
        rootMargin: '0px 0px 200px 0px',
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [fetchMore, hasMore, isFullPage]);

  return (
    <Box
      sx={{
        pb: isFullPage
          ? { xs: 14, md: 12 }
          : isOnsaMarketPage
            ? { xs: 4, md: 5 }
            : 0
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          p: 2,
          border: isFullPage ? 'none' : { xs: 'none', sm: '1px solid #e0e0e0' },
          borderRadius: isFullPage ? 0 : { xs: 0, sm: 2 },
          bgcolor: isFullPage ? 'transparent' : { xs: 'white', sm: '#f5f5f5' },
          mb: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent={isFullPage ? 'space-between' : { xs: 'center', sm: 'space-between' }}
          alignItems="center"
        >
          <Typography
            variant={isFullPage ? 'h5' : 'h6'}
            fontWeight="bold"
            textAlign={isFullPage ? 'left' : { xs: 'center', sm: 'left' }}
          >
            <Link href="/news" underline="hover" color="inherit">
              News
            </Link>
          </Typography>

          {isFullPage ? (
            <IconButton size="small" color="inherit" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          ) : (
            !isMobile && (
              <Link href="/news" underline="always" color="inherit">
                See more
              </Link>
            )
          )}
        </Stack>
      </Box>

      {/* CONTENT */}
      {isFullPage ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            px: { xs: 2, md: 2 },
            overflowX: 'hidden',
          }}
        >
          {news.map((item, i) => (
            <Paper
              key={makeItemKey(item) || i}
              variant="outlined"
              sx={{
                bgcolor: cardBg,
                borderColor: cardLine,
                borderRadius: 2,
                p: 1.5,
                boxShadow: '0 6px 16px rgba(2,6,23,.06)',
                transition: 'box-shadow .2s, transform .2s',
                '&:hover': {
                  boxShadow: '0 10px 24px rgba(2,6,23,.10)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <NewsCard
                title={item.title}
                summary={item.summary}
                image={item.banner_image}
                url={item.url}
                publishedAt={item.published_at}
                source={item.source}
                size="lg"
              />
            </Paper>
          ))}
        </Box>
      ) : (
        <Stack spacing={spacing} sx={{ mt: offsetTop ? { xs: 0, sm: 1 } : 0 }}>
          {news.map((item, i) => (
            <Paper
              key={makeItemKey(item) || i}
              variant="outlined"
              sx={{
                bgcolor: cardBg,
                borderColor: cardLine,
                borderRadius: compactWidget ? 1.5 : 2,
                p: compactWidget ? 0 : 1.5,
                boxShadow: compactWidget
                  ? '0 2px 8px rgba(2,6,23,.05)'
                  : '0 4px 12px rgba(2,6,23,.06)',
              }}
            >
              <NewsCard
                title={item.title}
                summary={item.summary}
                image={item.banner_image}
                url={item.url}
                publishedAt={item.published_at}
                source={item.source}
                size={compactWidget ? 'sm' : 'md'}
              />
            </Paper>
          ))}
        </Stack>
      )}

      {isFullPage && (
        <Box
          ref={sentinelRef}
          sx={{
            mt: 3,
            textAlign: 'center',
            px: { xs: 2, md: 2 },
            pb: 4,
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {loading && <CircularProgress size={24} thickness={4} />}
          {!loading && news.length === 0 && !error && (
            <Typography variant="body2" color="text.secondary">
              No news available yet.
            </Typography>
          )}
          {!loading && !hasMore && news.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              No more news to display.
            </Typography>
          )}
          {error && (
            <Typography color="error">
              {error}
            </Typography>
          )}
        </Box>
      )}

      {!isFullPage && error && (
        <Typography color="error" sx={{ mt: 2, px: 2 }}>
          {error}
        </Typography>
      )}

      {!isFullPage && isMobile && (
        <Box mt={2} textAlign="center">
          <Link href="/news" underline="always" color="text.primary">
            See more
          </Link>
        </Box>
      )}
    </Box>
  );
};

export default NewsPreview;
